// src/components/wellfinder/HolderChip.jsx
//
// What the map is showing when the analyst asks who holds a taken candidate,
// in words, with the one comparison they came for: how close the HOLDER sits
// to the well against how close THIS detection does. Both distances come from
// the same kind of candidate row, measured from each detection as delivered,
// so they compare.
//
// ── OPEN RECORD IS A HISTORY PUSH ────────────────────────────────────────────
// Not `replace`, which is what the page's own URL writes use. Those exist so
// advancing through a hundred records does not bury the back button; this is
// a detour the analyst means to come back from, and Back is how they will.
// The page's ?record= effect does the opening, through select(), so nothing
// staged on this record follows them there.

import { useNavigate } from "@tanstack/react-router";
import { ArrowDownToLine, ExternalLink, X } from "lucide-react";
import { holderWording } from "./holderWording";
import { fmtDistance } from "./fmtDistance";

// `onMove`: opens the move confirmation for this candidate, or null where this
// record cannot take the well (it already carries a verdict — reopen first).
export default function HolderChip({ candidate, onHide, onMove = null }) {
  const navigate = useNavigate();
  const w = holderWording(candidate);
  if (!w) return null;
  const c = candidate;

  return (
    <div
      data-testid="holder-chip"
      className="absolute left-2 top-2 z-10 max-w-[70%] rounded border border-yellow-300/50 bg-bg/90 px-2 py-1.5 text-[11px] backdrop-blur-sm"
    >
      <div className="flex items-start gap-2">
        <p className="min-w-0 flex-1 text-fg-primary">
          <span className="text-fg-secondary">#{c.rank} is {w.verb} </span>
          <span className="break-all font-mono">{w.id}</span>
        </p>
        <button
          type="button"
          onClick={onHide}
          aria-label="Hide the holding detection"
          className="shrink-0 rounded p-0.5 text-fg-secondary hover:bg-white/[0.06] hover:text-fg-primary"
        >
          <X size={12} />
        </button>
      </div>
      {w.detail && <p className="text-fg-secondary">{w.detail}</p>}
      <p data-testid="holder-chip-distances" className="mt-0.5 tabular-nums text-fg-secondary">
        <span className="text-yellow-300">{fmtDistance(c.taken_by_distance_m)}</span>
        {" from this well · this detection: "}
        <span className="text-fg-primary">{fmtDistance(c.distance_m)}</span>
      </p>
      {c.taken_by_moved && (
        // Six holders today. The point is where the analyst put it; the
        // distance is still from where the model did, so the line on the map
        // and the number here disagree, and this says why.
        <p className="mt-0.5 text-[10px] text-fg-secondary/80">
          Plotted at an analyst's corrected point; the distance is from the
          original detection.
        </p>
      )}
      <div className="mt-1 flex flex-wrap items-center gap-1.5">
      {onMove && (
        <button
          type="button"
          data-testid="holder-chip-move"
          onClick={onMove}
          className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-yellow-300 ring-1 ring-yellow-300/40 hover:bg-yellow-300/10"
        >
          <ArrowDownToLine size={11} />
          Move the well here
        </button>
      )}
      <button
        type="button"
        data-testid="holder-chip-open"
        onClick={() => navigate({
          to: "/workspace/review",
          search: (prev) => ({ ...prev, record: c.taken_by_record_id }),
        })}
        className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-fg-primary ring-1 ring-white/15 hover:bg-white/[0.06]"
      >
        <ExternalLink size={11} />
        Open record
      </button>
      </div>
    </div>
  );
}
